import React from "react";

import { TimePicker } from "@npm_leadtech/qr-ui-components";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { DatePicker } from "@/components/DatePicker";
import { InputAdapter } from "@/components/Input/adapter/InputAdapter";
import { TimezoneSelector } from "@/components/TimezoneSelector";
import { EventDateAndTimeViewProps } from "./types";
import { PAGES } from "@/internals/constants/pages";
import { NAMESPACES } from "@/internals/constants/namespaces";

export const EventDateAndTimeView = (props: EventDateAndTimeViewProps) => {
  const {
    ctaStyle,
    format,
    isAllDay,
    startMinDate,
    startDate,
    endDate,
    startTimeValue,
    endTimeValue,
    timezone,
    actionTitle,
    onFormatChange,
    onAllDayChange,
    onStartDateChange,
    onEndDateChange,
    onStartTimeChange,
    onEndTimeChange,
    onClearStartTime,
    onClearEndTime,
    onTimezoneChange,
    onActionTitleChange,
  } = props;

  const { t: translate } = useTranslation(PAGES.QR_EVENT);
  const { t: timePickerTF } = useTranslation(NAMESPACES.TIME_PICKER);

  const use12Hours = format === "AM-PM";
  const timePlaceholder = use12Hours ? "00:00AM" : "00:00";

  return (
    <div className="event-date-time">
      <div className="event-date-time__format">
        <Button
          type="button"
          label={timePickerTF("time_format_am_pm")}
          tertiary
          onClick={() => onFormatChange("AM-PM")}
          rounded={!!ctaStyle}
          className={`${format === "24-HOURS" && "hours__format--disabled"}`}
        />

        <Button
          type="button"
          label={timePickerTF("time_format_24")}
          tertiary
          onClick={() => onFormatChange("24-HOURS")}
          rounded={!!ctaStyle}
          className={`${format === "AM-PM" && "hours__format--disabled"}`}
        />
      </div>

      <div className="event-date-time__date-time-row">
        <DatePicker
          type="single"
          minDate={startMinDate}
          label={translate("time_start_label")}
          inputPlaceholder={translate("time_start_placeholder")}
          defaultDate={startDate ? [startDate] : []}
          fluid
          disableMobile
          className={"event-date-time__date-picker"}
          onChangeDate={([date] = []) => onStartDateChange(date)}
        />

        {!isAllDay && (
          <div className="event-date-time__time-picker">
            <TimePicker
              dataQA="event-time-start"
              placeholder={timePlaceholder}
              disabled={isAllDay}
              use12Hours={use12Hours}
              needConfirm={true}
              allowClear
              value={startTimeValue}
              onChange={onStartTimeChange}
              onClear={onClearStartTime}
              ariaLabel={translate("time_start_label")}
            />
          </div>
        )}
      </div>

      <div className="event-date-time__date-time-row">
        <DatePicker
          type="single"
          minDate={startDate ?? "today"}
          label={translate("time_end_label")}
          inputPlaceholder={translate("time_end_placeholder")}
          defaultDate={endDate ? [endDate] : []}
          fluid
          disableMobile
          className={"event-date-time__date-picker"}
          onChangeDate={([date] = []) => onEndDateChange(date)}
        />

        {!isAllDay && (
          <div className="event-date-time__time-picker">
            <TimePicker
              dataQA="event-time-end"
              placeholder={timePlaceholder}
              disabled={isAllDay}
              use12Hours={use12Hours}
              needConfirm={true}
              allowClear
              value={endTimeValue}
              onChange={onEndTimeChange}
              onClear={onClearEndTime}
              ariaLabel={translate("time_end_label")}
            />
          </div>
        )}
      </div>

      <div className="event-date-time__timezone-row">
        <Checkbox
          id={"event-all-day-checkbox"}
          label={translate("time_all_day_label")}
          className="event-date-time__timezone-checkbox"
          defaultValue={isAllDay}
          onChange={(e) => onAllDayChange(e.target.checked)}
        />
        {!isAllDay && (
          <TimezoneSelector
            placeholder={translate("timezone_placeholder")}
            label={translate("time_timezone_label")}
            value={timezone}
            searchPlaceholder={translate("timezone_search_placeholder")}
            onChange={onTimezoneChange}
          />
        )}
      </div>

      <InputAdapter
        id="timeActionTitle"
        type="text"
        name="timeActionTitle"
        dataQA="timeActionTitle"
        label={translate("time_button_label")}
        placeholder={translate("time_button_placeholder")}
        defaultValue={actionTitle}
        helper={{
          item: "help",
          tooltip: {
            text: translate("time_button_tooltip"),
            position: "top",
          },
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onActionTitleChange(e.target.value)}
      />
    </div>
  );
};
